import { LoadingSpinner } from "components/LoadingSpinner";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useMemo, useRef, useState } from "react";
import {
  useGetBulkAppealOpportunitiesQuery,
  useLazyGetBulkAppealLetterQuery,
} from "store/queries/claims";
import styled from "styled-components";
import { IBulkAppealClaimRow, IBulkAppealGroup } from "types/claim";

interface IBulkAppealsDashboardProps {
  accountId: string;
}

interface IPayerAggregate {
  key: string;
  payerName: string | null;
  planLabel: string | null;
  recoverable: number;
  claimCount: number;
  groups: IBulkAppealGroup[];
}

const Wrapper = styled.div`
  padding: 0.5rem 0.25rem 2rem;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Eyebrow = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
`;

const Title = styled.h2`
  margin: 0 0 0.35rem;
  font-size: 1.6rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #6c757d;
  max-width: 640px;
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const KpiCard = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  background: #fff;
`;

const KpiLabel = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
`;

const KpiValue = styled.div<{ $accent?: boolean }>`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${(props) => (props.$accent ? "#2e7d32" : "#212529")};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const ToolbarTitle = styled.div`
  font-weight: 700;
  font-size: 1.05rem;
`;

const PayerCard = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 12px;
  margin-bottom: 0.75rem;
  overflow: hidden;
  background: #fff;
`;

const RowButton = styled.button<{ $indent?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: ${(props) =>
    props.$indent ? "0.85rem 1.25rem 0.85rem 2.5rem" : "1rem 1.25rem"};
  background: none;
  border: none;
  border-top: ${(props) => (props.$indent ? "1px solid #f1f3f5" : "none")};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f8f9fa;
  }
`;

const Chevron = styled.i`
  color: #adb5bd;
  font-size: 0.8rem;
  width: 0.9rem;
`;

const RowMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowTitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
`;

const RowSub = styled.div`
  font-size: 0.82rem;
  color: #6c757d;
  margin-top: 0.15rem;
`;

const Recoverable = styled.div`
  text-align: right;
  white-space: nowrap;
`;

const RecoverableValue = styled.div`
  color: #2e7d32;
  font-weight: 700;
  font-size: 1.05rem;
`;

const RecoverableLabel = styled.div`
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  color: #adb5bd;
  text-transform: uppercase;
`;

const PlanBadge = styled.span`
  font-size: 0.7rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.1rem 0.4rem;
  color: #6c757d;
`;

const ClaimsWrapper = styled.div`
  padding: 0 1.25rem 1rem 2.5rem;
`;

const AppealAction = styled.div`
  padding-left: 0.5rem;
`;

const LetterSubject = styled.div`
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const LetterBody = styled.pre`
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.5;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  max-height: 55vh;
  overflow: auto;
  margin: 0;
`;

const formatUsd = (amount: number): string =>
  `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatUsd0 = (amount: number): string =>
  `$${Math.round(amount).toLocaleString("en-US")}`;

const toggle = (list: string[], key: string): string[] =>
  list.includes(key) ? list.filter((k) => k !== key) : [...list, key];

export function BulkAppealsDashboard({
  accountId,
}: IBulkAppealsDashboardProps) {
  const toastRef = useRef<Toast>(null);
  const [expandedPayers, setExpandedPayers] = useState<string[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<IBulkAppealGroup | null>(
    null
  );

  const { data: groups, isLoading } = useGetBulkAppealOpportunitiesQuery(
    { accountId },
    { skip: !accountId }
  );

  const [fetchLetter, { data: letter, isFetching: isLetterLoading }] =
    useLazyGetBulkAppealLetterQuery();

  const payers: IPayerAggregate[] = useMemo(() => {
    const map = new Map<string, IPayerAggregate>();
    (groups || []).forEach((group) => {
      const key = String(group.payerId ?? "none");
      let payer = map.get(key);
      if (!payer) {
        payer = {
          key,
          payerName: group.payerName,
          planLabel: group.planLabel,
          recoverable: 0,
          claimCount: 0,
          groups: [],
        };
        map.set(key, payer);
      }
      payer.groups.push(group);
      payer.recoverable += group.totalUnderpaymentDollars;
      payer.claimCount += group.claimCount;
      if (!payer.planLabel && group.planLabel) {
        payer.planLabel = group.planLabel;
      }
    });

    const list = [...map.values()];
    list.sort((a, b) => b.recoverable - a.recoverable);
    list.forEach((payer) =>
      payer.groups.sort(
        (a, b) => b.totalUnderpaymentDollars - a.totalUnderpaymentDollars
      )
    );
    return list;
  }, [groups]);

  const kpis = useMemo(() => {
    const rows = groups || [];
    return {
      recoverable: rows.reduce((sum, g) => sum + g.totalUnderpaymentDollars, 0),
      claimsFlagged: rows.reduce((sum, g) => sum + g.claimCount, 0),
      payers: payers.length,
      classifications: new Set(rows.map((g) => g.classification)).size,
    };
  }, [groups, payers]);

  const groupKey = (payerKey: string, group: IBulkAppealGroup) =>
    `${payerKey}__${group.classification}`;

  const openLetter = (group: IBulkAppealGroup) => {
    setSelectedGroup(group);
    fetchLetter({
      accountId,
      classification: group.classification,
      payerId: group.payerId,
    });
  };

  const copyLetter = async () => {
    if (!letter) {
      return;
    }
    try {
      await navigator.clipboard.writeText(
        `${letter.subject}\n\n${letter.body}`
      );
      toastRef.current?.show({
        severity: "success",
        summary: "Copied",
        detail: "Appeal letter copied to clipboard.",
        life: 3000,
      });
    } catch {
      toastRef.current?.show({
        severity: "error",
        summary: "Copy failed",
        detail: "Could not copy the letter to the clipboard.",
        life: 5000,
      });
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Wrapper>
      <Toast ref={toastRef} />

      <Header>
        <div>
          <Eyebrow>Revenue Integrity</Eyebrow>
          <Title>Bulk Underpayment Appeals</Title>
          <Subtitle>
            Underpayment opportunities grouped by payer and denial
            classification. Review affected claims and generate a bulk appeal
            letter for each pattern.
          </Subtitle>
        </div>
      </Header>

      <KpiRow>
        <KpiCard>
          <KpiLabel>Total underpayment</KpiLabel>
          <KpiValue $accent>{formatUsd0(kpis.recoverable)}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Claims flagged</KpiLabel>
          <KpiValue>{kpis.claimsFlagged.toLocaleString("en-US")}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Payers</KpiLabel>
          <KpiValue>{kpis.payers}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Denial classifications</KpiLabel>
          <KpiValue>{kpis.classifications}</KpiValue>
        </KpiCard>
      </KpiRow>

      <Toolbar>
        <ToolbarTitle>Opportunities by payer</ToolbarTitle>
      </Toolbar>

      {payers.length === 0 && (
        <KpiCard>No bulk appeal opportunities for this account.</KpiCard>
      )}

      {payers.map((payer) => {
        const payerOpen = expandedPayers.includes(payer.key);
        return (
          <PayerCard key={payer.key}>
            <RowButton
              type="button"
              onClick={() =>
                setExpandedPayers((prev) => toggle(prev, payer.key))
              }
            >
              <Chevron
                className={`pi ${
                  payerOpen ? "pi-chevron-down" : "pi-chevron-right"
                }`}
              />
              <i className="pi pi-building" style={{ color: "#868e96" }} />
              <RowMain>
                <RowTitleLine>
                  {payer.payerName || "No Payer Match"}
                  {payer.planLabel && <PlanBadge>{payer.planLabel}</PlanBadge>}
                </RowTitleLine>
                <RowSub>
                  {payer.groups.length} denial classification
                  {payer.groups.length === 1 ? "" : "s"} · {payer.claimCount}{" "}
                  claims
                </RowSub>
              </RowMain>
              <Recoverable>
                <RecoverableValue>
                  {formatUsd0(payer.recoverable)}
                </RecoverableValue>
                <RecoverableLabel>Underpayment</RecoverableLabel>
              </Recoverable>
            </RowButton>

            {payerOpen &&
              payer.groups.map((group) => {
                const key = groupKey(payer.key, group);
                const groupOpen = expandedGroups.includes(key);
                return (
                  <div key={key}>
                    <RowButton
                      type="button"
                      $indent
                      onClick={() =>
                        setExpandedGroups((prev) => toggle(prev, key))
                      }
                    >
                      <Chevron
                        className={`pi ${
                          groupOpen ? "pi-chevron-down" : "pi-chevron-right"
                        }`}
                      />
                      <RowMain>
                        <RowTitleLine>{group.classification}</RowTitleLine>
                        <RowSub>
                          {group.claimCount} claims ·{" "}
                          {formatUsd0(group.totalUnderpaymentDollars)} underpaid
                        </RowSub>
                      </RowMain>
                      <AppealAction
                        role="presentation"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          label="Appeal letter"
                          icon="pi pi-file"
                          className="p-button-outlined p-button-sm"
                          onClick={() => openLetter(group)}
                        />
                      </AppealAction>
                    </RowButton>

                    {groupOpen && (
                      <ClaimsWrapper>
                        <DataTable
                          value={group.claims}
                          responsiveLayout="scroll"
                          size="small"
                        >
                          <Column field="claimNumber" header="Claim #" />
                          <Column
                            header="DOS"
                            body={(claim: IBulkAppealClaimRow) =>
                              claim.serviceDate
                                ? String(claim.serviceDate).slice(0, 10)
                                : "-"
                            }
                          />
                          <Column
                            header="CPT"
                            body={(claim: IBulkAppealClaimRow) =>
                              claim.code || "-"
                            }
                          />
                          <Column
                            header="Expected"
                            body={(claim: IBulkAppealClaimRow) =>
                              formatUsd(claim.expectedDollars)
                            }
                          />
                          <Column
                            header="Paid"
                            body={(claim: IBulkAppealClaimRow) =>
                              formatUsd(claim.paidDollars)
                            }
                          />
                          <Column
                            header="Underpaid"
                            body={(claim: IBulkAppealClaimRow) =>
                              formatUsd(claim.underpaymentDollars)
                            }
                          />
                        </DataTable>
                      </ClaimsWrapper>
                    )}
                  </div>
                );
              })}
          </PayerCard>
        );
      })}

      <Dialog
        header={
          selectedGroup
            ? `Appeal Letter — ${selectedGroup.classification}`
            : "Appeal Letter"
        }
        visible={selectedGroup !== null}
        style={{ width: "min(760px, 92vw)" }}
        onHide={() => setSelectedGroup(null)}
        footer={
          <Button
            label="Copy"
            icon="pi pi-copy"
            onClick={copyLetter}
            disabled={!letter || isLetterLoading}
          />
        }
      >
        {isLetterLoading || !letter ? (
          <LoadingSpinner />
        ) : (
          <>
            <LetterSubject>Subject: {letter.subject}</LetterSubject>
            <LetterBody>{letter.body}</LetterBody>
          </>
        )}
      </Dialog>
    </Wrapper>
  );
}
